import {
  BanknotesIcon,
  CalculatorIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  CogIcon,
  CreditCardIcon,
  DocumentTextIcon,
  HomeIcon,
  PhoneIcon,
  ReceiptRefundIcon,
  TruckIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { staffPortalRequestsApi } from '../../services/staffPortalRequestsApi';
import { normalizeRole, type CanonicalRole } from '../../utils/roles';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Deal Calculator', href: '/calculator', icon: CalculatorIcon },
  { name: 'Customers', href: '/customers', icon: UserGroupIcon },
  { name: 'Inventory', href: '/vehicles', icon: TruckIcon },
  {
    name: 'Inventory Costs',
    href: '/inventory/costs',
    icon: BanknotesIcon,
    roles: [
      'system_admin',
      'general_manager',
      'finance_manager',
      'sales_manager',
    ] as CanonicalRole[],
  },
  { name: 'Deals', href: '/deals', icon: ReceiptRefundIcon },
  { name: 'BHPH Dashboard', href: '/bhph/dashboard', icon: CreditCardIcon },
  { name: 'BHPH Accounts', href: '/bhph/accounts', icon: ClipboardDocumentListIcon },
  { name: 'Portal Requests', href: '/bhph/portal-requests', icon: ChatBubbleLeftRightIcon },
  { name: 'CRM', href: '/crm', icon: PhoneIcon },
  { name: 'Credit Apps', href: '/credit-applications', icon: ClipboardDocumentCheckIcon },
  // GPS Tracking removed from top-level nav (P5-D1, amended 2026-05-20): GPS is
  // core BHPH functionality and stays on the BHPH deal-detail page; the
  // standalone /gps fleet dashboard remains reachable by direct URL.
  { name: 'Document Center', href: '/documents', icon: DocumentTextIcon },
  {
    name: 'Audit Logs',
    href: '/audit-logs',
    icon: ClipboardDocumentListIcon,
    roles: ['developer'] as CanonicalRole[],
  },
  {
    name: 'SuperTracker',
    href: '/tracker',
    icon: ChartBarIcon,
    roles: ['developer'] as CanonicalRole[],
  },
  {
    name: 'Users',
    href: '/users',
    icon: UsersIcon,
    roles: [
      'system_admin',
      'general_manager',
      'sales_manager',
      'finance_manager',
    ] as CanonicalRole[],
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: CogIcon,
    roles: ['system_admin'] as CanonicalRole[],
  },
];

interface SidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed = false, onToggle }) => {
  const { user } = useAuth();
  const { theme } = usePreferences();

  // v2 servicing Slice 10: awaiting-staff alert on the BHPH Dashboard item.
  // Shows awaiting_staff ONLY -- never a sum with pending_promises (Jose
  // 2026-07-20) -- and is visible to every staff role (no `roles` gate).
  const { data: portalRequestCounts } = useQuery({
    queryKey: ['staffPortalRequestCounts'],
    queryFn: () => staffPortalRequestsApi.counts(),
    refetchInterval: 30000,
  });
  const awaitingStaffCount = portalRequestCounts?.awaiting_staff ?? 0;

  // Use light icon on dark backgrounds, dark icon on light backgrounds
  const logoSrc = theme === 'dark' ? '/images/favicon-light.png' : '/images/favicon-dark.png';

  const currentRole = normalizeRole(user?.role);
  const filteredNavigation = navigation.filter(
    (item) => !item.roles || (user && item.roles.includes(currentRole))
  );

  return (
    <div
      className={`flex flex-col h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Logo */}
      <div
        className={`flex items-center h-16 border-b border-gray-200 dark:border-gray-700 ${
          isCollapsed ? 'px-2 justify-between' : 'px-4'
        }`}
      >
        {!isCollapsed && (
          <div className="flex items-center min-w-0 flex-1">
            <img src={logoSrc} alt="CarLotPilot" className="h-10 w-auto object-contain" />
            <span className="ml-2 text-lg font-semibold text-gray-900 dark:text-white truncate">
              CarLotPilot
            </span>
          </div>
        )}

        {isCollapsed && <img src={logoSrc} alt="CarLotPilot" className="h-8 w-8 object-contain" />}

        {/* Toggle Button */}
        {onToggle && (
          <button
            onClick={onToggle}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRightIcon className="h-4 w-4" />
            ) : (
              <ChevronLeftIcon className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-6 space-y-1 custom-scrollbar overflow-y-auto">
        {filteredNavigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            className={({ isActive }) =>
              `group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
              }`
            }
            title={isCollapsed ? item.name : undefined}
          >
            <span className="relative flex-shrink-0">
              <item.icon className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'}`} />
              {item.name === 'BHPH Dashboard' && awaitingStaffCount > 0 && isCollapsed && (
                <span
                  aria-label={`${awaitingStaffCount} awaiting staff response`}
                  className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500 ring-1 ring-white dark:ring-gray-800"
                />
              )}
            </span>
            {!isCollapsed && <span className="truncate">{item.name}</span>}
            {!isCollapsed && item.name === 'BHPH Dashboard' && awaitingStaffCount > 0 && (
              <span
                aria-label={`${awaitingStaffCount} awaiting staff response`}
                className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white"
              >
                {awaitingStaffCount > 99 ? '99+' : awaitingStaffCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User info */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-3">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-gray-700 dark:text-gray-200 text-sm font-medium">
              {user?.first_name?.[0]}
              {user?.last_name?.[0]}
            </span>
          </div>
          {!isCollapsed && (
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
